import axios from 'axios';
import { store } from '@/store';
import router from '@/router';
import { httpCode } from '@/config/httpCode';
import loading from '@/helpers/loading';
import { MESSAGE_MAP } from '@/config/message';

let http = axios.create({
  baseURL: process.env.VUE_APP_API_ENDPOINT,
  withCredentials: false,
  timeout: 30000,
});

let failedQueue = [];
let isRefreshing = false;
let timeoutId;

http.interceptors.request.use((config) => {
  const token = store.getters['auth/token'];
  if (!config.hasCustomConfig) {
    config.headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      // 'Sub-Domain': window.location.host.split('.')[0],
      'Sub-Domain': localStorage.getItem('SUBDOMAIN') || window.location.host.split('.')[0],
      Authorization: token ? `Bearer ${token}` : '',
    };
  }
  if (config.method === 'post' || config.method === 'put' || config.method === 'delete' || config.isLoading) {
    store.dispatch('common/setButtonLoading', true);
    if (!isRefreshing) {
      timeoutId = loading.show();
    }
  }
  store.dispatch('common/resetErrorFields');
  return config;
});

function showErrors(errors) {
  store.dispatch('common/setErrors', errors);
  store.dispatch('common/openErrorModal', true);
}

function setErrorFields(errors) {
  if (!errors) {
    return {};
  }
  let apiInputErrors = {};
  if (Array.isArray(errors)) {
    errors.map((item) => {
      apiInputErrors[item.name] = true;
    });
  }
  return apiInputErrors;
}

function normalizeError(errors) {
  if (!errors) {
    return [];
  }
  if (Array.isArray(errors)) {
    return errors.map((item) => item.message);
  }
  return [errors.message];
}

const processQueue = (error) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve();
    }
  });
  failedQueue = [];
};

const redirectUrl = async () => {
  await Promise.all([
    store.dispatch('event/getParentEventInfo'),
    store.dispatch('event/getChildEventInfo', { directory: router.currentRoute.value?.params?.directory })
  ]);
  if (store.getters['event/childEventInfo']?.expiredEvent && store.getters['event/childEventInfo']?.urlFaqCustomer) {
    window.location.href = store.getters['event/childEventInfo']?.urlFaqCustomer + '?timestamp=' + Date.now();
  } else if (store.getters['event/parentEventInfo']?.allChildEventEnd && store.getters['event/parentEventInfo']?.urlFaqCustomer) {
    window.location.href = store.getters['event/parentEventInfo']?.urlFaqCustomer + '?timestamp=' + Date.now();
  } else {
    router.push({ name: '404' });
  }
};

http.interceptors.response.use(
  (response) => {
    store.dispatch('common/setButtonLoading', false);
    if (timeoutId) {
      loading.clear(timeoutId);
    }
    return response;
  },
  async (error) => {
    store.dispatch('common/setButtonLoading', false);
    if (timeoutId) {
      loading.clear(timeoutId);
    } else {
      store.dispatch('common/setLoading', false);
    }
    const errors = normalizeError(error?.response?.data?.errors);
    const originalRequest = error.config;
    switch (error?.response?.status) {
      case httpCode.SYSTEM_ERROR:
        router.push({ name: '500' });
        break;
      case httpCode.NOT_FOUND:
        if (error?.response?.data?.errors[0]?.name === 'redirect') {
          redirectUrl();
        } else {
          router.push({ name: '404' });
        }
        break;
      case httpCode.UNAUTHORIZED:
        if (originalRequest.authenticationApi) {
          if (router.currentRoute.value.name !== 'Authentication') {
            await router.push({ name: 'Authentication' });
          }
          store.dispatch('common/openTermsModal', false);
          showErrors(error?.response?.data);
        } else if (
          !originalRequest._retry &&
          !originalRequest.notRefresh
        ) {
          if (originalRequest?.closeTermsOfService) {
            store.dispatch('common/openTermsModal', false);
          }
          const rememberFlag = JSON.parse(localStorage.getItem('rememberFlag'));
          if ((router.currentRoute.value?.name ? !router.currentRoute.value?.meta?.authRequired : originalRequest.params?.isHome) && !rememberFlag) {
            await store.dispatch('auth/resetAuth');
            if (originalRequest?.closeUseCoupon) {
              router.push({ name: JSON.parse(sessionStorage.getItem('isFromLoginSms')) ? 'LoginSmsParent' : 'LoginParent', query: { back: true } }).then(() => {
                store.dispatch('common/openErrorModal', true);
                store.dispatch('common/setErrors', [MESSAGE_MAP.outOfLoginSession]);
                store.dispatch('common/setModalBackToRoute', null);
              }).catch(() => {});
            }
            return;
          }
          if (isRefreshing) {
            return new Promise(function(resolve, reject) {
              failedQueue.push({ resolve, reject });
            })
              .then(() => {
                return http(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }
          originalRequest._retry = true;
          isRefreshing = true;
          return new Promise(function(resolve) {
            return store
              .dispatch('auth/refreshToken')
              .then(() => {
                processQueue();
                resolve(http(originalRequest));
              })
              .catch(async (error) => {
                processQueue(error);
                if (store.getters['auth/token']) {
                  const qrRouteData = store.getters['common/qrRouteData'];
                  const newDirectory = qrRouteData.meta?.isScanQR ? qrRouteData.params?.directory : router.currentRoute.value?.params.directory;
                  store.dispatch('auth/resetAuth');
                  const loginRouteName = newDirectory ? 'Login' : 'LoginParent';
                  await router.push({ name: loginRouteName, params: qrRouteData.params, query: qrRouteData.query?.backToTop ? {} : { ...qrRouteData.query, redirect: qrRouteData.name } });
                  showErrors(errors);
                }
              })
              .finally(() => {
                isRefreshing = false;
              });
          });
        }
        break;
      case httpCode.BAD_REQUEST: {
        const apiInputErrors = setErrorFields(error?.response?.data?.errors);
        if (originalRequest.config?.noError) {
          return Promise.reject(errors);
        }
        store.dispatch('common/setErrorFields', apiInputErrors);
        showErrors(errors);
        if (originalRequest?.checkBlockedSms) {
          store.dispatch('event/setBlockUser', error?.response?.data?.isBlockedSms);
          if (error?.response?.data?.forceLogout) await store.dispatch('common/setBackRouteName', 'Login');
        }
        if (originalRequest?.checkClearQueryUrl) {
          if (['Mypage', 'EventList'].includes(router.currentRoute.value.name)) {
            const url = window.location.origin + window.location.pathname + window.location.hash.split('?')[0];
            window.history.replaceState(null, null, url);
          }
        }
        if (error?.response?.data?.errors[0]?.name === 'isRedirectToForgotScreen') {
          store.dispatch('common/setBackRouteName', 'PasswordSms');
          sessionStorage.setItem('isDoubleBack', true);
        }
        if (error?.response?.data?.errors[0]?.name === 'isRedirectToEntry') {
          localStorage.removeItem('newRegistInfo');
          store.dispatch('customer/resetRegistInfo');
          store.dispatch('customer/resetCpmDetail');
          store.dispatch('common/setBackRouteName', router.currentRoute.value.name.includes('Sms') ? 'Entry' : 'EntrySms');
        }
        break;
      }
      case httpCode.FORBIDDEN: {
        if (router.currentRoute.value.name.includes('MonitoringEntry')) {
          store.dispatch('common/setModalBackToRoute', 'Monitoring');
        }
        if (router.currentRoute.value.name.includes('PointHistory')) {
          store.dispatch('common/setModalBackToRoute', 'Mypage');
        }
        showErrors(errors);
        break;
      }
      default:
        break;
    }
    return Promise.reject(errors);
  }
);

export default http;
